import * as React from 'react';

/*
 * HeroChart — the full-bleed cumulative area chart (Overview archetype).
 * Pure SVG, no chart library: smooth Catmull-Rom paths, gradient fills,
 * non-scaling 2.2px strokes. The chart deliberately overflows the
 * content grid (ref: Visitors) while the axis keeps the page padding;
 * endpoint dots are HTML-positioned so preserveAspectRatio="none"
 * can't distort them.
 */

export interface ChartSeries {
    label: string;
    /* css color, e.g. 'hsl(var(--brand))' */
    color: string;
    /* cumulative values, one per bucket */
    values: number[];
}

export interface AxisTick {
    label: string;
    strong?: boolean;
}

interface HeroChartProps {
    series: ChartSeries[];
    axis: AxisTick[];
}

const W = 1000;
const H = 210;
const TOP_PAD = 10;

/* Catmull-Rom → cubic bezier through every point */
function smoothPath(pts: { x: number; y: number }[]) {
    if (pts.length < 2) return '';
    let d = `M${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] ?? pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] ?? p2;
        const c1x = p1.x + (p2.x - p0.x) / 6;
        const c1y = p1.y + (p2.y - p0.y) / 6;
        const c2x = p2.x - (p3.x - p1.x) / 6;
        const c2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C${c1x} ${c1y},${c2x} ${c2y},${p2.x} ${p2.y}`;
    }
    return d;
}

export default function HeroChart({ series, axis }: HeroChartProps) {
    const uid = React.useId().replace(/:/g, '');
    const max = Math.max(1, ...series.flatMap((s) => s.values));

    const toPoints = (values: number[]) =>
        values.map((v, i) => ({
            x: (i / (values.length - 1)) * W,
            y: TOP_PAD + (1 - v / max) * (H - TOP_PAD),
        }));

    return (
        <div className="relative left-1/2 w-[min(100vw,1500px)] -translate-x-1/2">
            {/* legend stays on the content grid */}
            <div className="mx-auto flex max-w-[1020px] justify-end gap-4 px-[clamp(18px,3vw,34px)] pb-1">
                {series.map((s) => (
                    <span
                        key={s.label}
                        className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground"
                    >
                        <span
                            className="block h-2 w-2 rounded-full"
                            style={{ background: s.color }}
                        />
                        {s.label}
                    </span>
                ))}
            </div>

            <div className="relative">
                <svg
                    viewBox={`0 0 ${W} ${H}`}
                    preserveAspectRatio="none"
                    className="block h-[210px] w-full"
                >
                    <defs>
                        {series.map((s, si) => (
                            <linearGradient
                                key={si}
                                id={`${uid}-g${si}`}
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="0"
                                    stopColor={s.color}
                                    stopOpacity="0.18"
                                />
                                <stop
                                    offset="1"
                                    stopColor={s.color}
                                    stopOpacity="0"
                                />
                            </linearGradient>
                        ))}
                    </defs>
                    {series.map((s, si) => {
                        const pts = toPoints(s.values);
                        const line = smoothPath(pts);
                        return (
                            <g key={si}>
                                <path
                                    d={`${line} L${W} ${H} L0 ${H} Z`}
                                    fill={`url(#${uid}-g${si})`}
                                />
                                <path
                                    d={line}
                                    fill="none"
                                    stroke={s.color}
                                    strokeWidth="2.2"
                                    vectorEffect="non-scaling-stroke"
                                />
                            </g>
                        );
                    })}
                </svg>
                {/* endpoint dots — HTML so they stay round */}
                {series.map((s, si) => {
                    const v = s.values[s.values.length - 1];
                    const y = TOP_PAD + (1 - v / max) * (H - TOP_PAD);
                    return (
                        <span
                            key={si}
                            className="absolute right-0 h-2 w-2 -translate-y-1/2 translate-x-1/2 rounded-full border-2 border-background"
                            style={{
                                top: `${(y / H) * 100}%`,
                                background: s.color,
                            }}
                        />
                    );
                })}
            </div>

            <div className="flex justify-between px-[clamp(18px,3vw,34px)] pt-1.5 font-mono text-[10.5px] text-faint">
                {axis.map((t, i) => (
                    <span
                        key={i}
                        className={
                            t.strong ? 'font-semibold text-foreground' : ''
                        }
                    >
                        {t.label}
                    </span>
                ))}
            </div>
        </div>
    );
}
